/**
 * Certification badges shown on /security.
 *
 * ACCURACY RULE (2026-09-09 owner ruling — sweep, not patch). Prest0 does NOT
 * hold its own SOC 2 Type II or ISO 27001 audit. It runs on Google Cloud,
 * which does. src/pages/Security.tsx has always said so internally ("GCP
 * inherited SOC 2 TYPE II + ISO 27001 with planned pen tests" — note
 * PLANNED), while these tooltips claimed "independent auditors review OUR
 * security controls all year" and Terms claimed "ongoing penetration tests".
 * A law firm verifies exactly this during procurement, and an overstatement
 * found there costs the deal.
 *
 * So: an inherited control is labelled as inherited, a contractual commitment
 * is labelled as contractual, and nothing here claims an audit we have not
 * had. When Prest0 does complete its own SOC 2, this file changes and the
 * claim becomes ours to make.
 */

export type CertificationBadge = {
  id: string;
  label: string;
  image: string;
  alt: string;
  tooltip: string;
  accent: string;
  size?: 'large';
};

export const certificationBadges: CertificationBadge[] = [
  {
    id: 'soc2-cert',
    label: 'SOC 2 TYPE II INFRASTRUCTURE',
    image: '/badge-soc2-type2.webp',
    alt: 'SOC 2 TYPE II badge',
    tooltip:
      'Prest0 runs on Google Cloud, which holds SOC 2 Type II for the infrastructure underneath us. Prest0 has not yet completed an audit of its own \u2014 we will say so here the day it does.',
    accent: 'rgba(198, 167, 94, 0.35)'
  },
  {
    id: 'iso-cert',
    label: 'ISO/IEC 27001 INFRASTRUCTURE',
    image: '/badge-iso-27001.webp',
    alt: 'ISO/IEC 27001 badge',
    tooltip:
      'The infrastructure we run on is certified to ISO/IEC 27001 by Google Cloud. Our own security program follows the same playbook; it is not separately certified.',
    accent: 'rgba(182, 146, 76, 0.32)'
  },
  {
    id: 'gdpr-cert',
    label: 'GDPR DPA AVAILABLE',
    image: '/badge-gdpr.webp',
    alt: 'GDPR badge',
    tooltip:
      'We sign DPAs, answer privacy requests, and pass our obligations down to sub-processors. Data is processed in the united states today and self-service export and erasure are still being built \u2014 ask us before assuming EU residency or full GDPR readiness.',
    accent: 'rgba(198, 167, 94, 0.28)',
    size: 'large'
  },
  {
    id: 'tls-cert',
    label: 'TLS 1.3 ENCRYPTED',
    image: '/badge-tls-13.webp',
    alt: 'TLS 1.3 badge',
    tooltip: 'Edge-to-core TLS 1.3 keeps traffic encrypted, so anyone listening on the wire only sees gibberish.',
    accent: 'rgba(26, 26, 26, 0.2)'
  },
  {
    id: 'zero-training-cert',
    label: 'ZERO AI TRAINING',
    image: '/badge-zero-training.webp',
    alt: 'ZERO AI training badge',
    tooltip: 'Your work is never used to train a model. This is a contractual commitment we hold our model providers to, on enterprise endpoints that do not retain prompts \u2014 not a switch in our own code.',
    accent: 'rgba(198, 167, 94, 0.32)'
  }
];
